<template>
  <div :class="$style.root">
    <div :class="$style.bg"></div>
    <div :class="$style.headerAction">
      <div>
        <PillButton
          v-if="metaConfig.showHeaderMainAction"
          :class="[showDisabledClass && $style.mainActionDisabled]"
          :icon="metaConfig.headerMainActionIconName"
          :disabled="metaConfig.headerMainActionDisabled || currentBroadcast?.is_automated_run_of_show"
          :uppercase="!showDisabledClass"
          data-testid="preview-popup-cta-btn"
          center
          size="x-small"
          @click="metaConfig.headerMainAction"
        >
          {{ $t(metaConfig.headerMainActionButtonText) }}
        </PillButton>
      </div>
      <IconButton
        :class="$style.actionButton"
        :icon="metaConfig.closeIconName"
        :ariaLabel="$t(metaConfig.closeButtonAriaLabel)"
        data-testid="survey-preview-cross-icon"
        variant="primary"
        size="x-small"
        alt
        @click="closeAction"
      />
    </div>
    <template v-if="isSurveyLoading">
      <Loading :class="$style.loading" size="small">
        <StyledText v-if="isSurveyUpdating" :class="$style.updating">
          {{ $t('@@translate@@ Updating Survey...') }}
        </StyledText>
      </Loading>
    </template>
    <template v-else-if="currentSurveyStatus === 'SUBMITTED'">
      <div :class="$style.submittedContent">
        <div :class="$style.submittedIcon">✓</div>
        <StyledText :class="$style.submittedMessage" variant="subHeading13">
          {{ survey.submit_message || $t('@@translate@@ Thank you for your response!') }}
        </StyledText>
      </div>
    </template>
    <template v-else>
      <div :class="$style.content">
        <div :class="$style.header">
          <StyledText v-if="survey.title" :class="$style.title" variant="heading14">
            {{ survey.title }}
          </StyledText>
          <StyledText v-if="survey.description" :class="$style.description" variant="subHeading13">
            {{ survey.description }}
          </StyledText>
        </div>
        <template v-for="(question, i) in survey.questions" :key="question.id + i">
          <component
            :is="componentName(question.question_type)"
            v-bind="question"
            data-testid="survey-questions"
            :answer="answers[question.id]"
            :showError="
              question.required &&
              !answers[question.id] &&
              (i <= survey.lastQuestionAnsweredIndex || survey.isSurveySubmitted)
            "
            @onAnswer="
              answer =>
                $store.dispatch('event/survey/onAnswer', {
                  surveyId,
                  question,
                  answer,
                  questionIndex: i
                })
            "
          />
          <div :class="$style.divider" />
        </template>
        <div :class="$style.actionBar">
          <PillButton
            :class="$style.submitButton"
            :disabled="metaConfig.isSubmitButtonDisabled"
            uppercase
            center
            data-testid="survey-submit-button"
            @click="onSubmitSurvey"
          >
            {{ $t('@@translate@@ Submit') }}
          </PillButton>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { SingleChoiceQuestion, MultipleChoiceQuestion, RatingQuestion, FreeFormTextQuestion } from 'uicomponents';

import loggerService from '@/services/logger.service';

export default {
  name: 'SurveyPreviewer',
  components: {
    SingleChoiceQuestion,
    MultipleChoiceQuestion,
    RatingQuestion,
    FreeFormTextQuestion
  },
  props: {
    surveyId: {
      type: String,
      required: true,
      default: ''
    }
  },
  computed: {
    survey() {
      return this.$store.state.event?.survey.surveys[this.surveyId];
    },
    answers() {
      return this.survey?.answerMap || {};
    },
    currentBroadcast() {
      return this.$store.getters['event/currentBroadcast'];
    },
    isBroadcastSpeakerOrOrganizer() {
      return this.$store.getters['event/isBroadcastSpeakerOrOrganizer'];
    },
    currentSurveyStatus() {
      return this.survey?.surveyStatus;
    },
    isSurveyUpdating() {
      return this.currentSurveyStatus === 'UPDATING';
    },
    isSurveyLoading() {
      return this.currentSurveyStatus === 'LOADING' || this.isSurveyUpdating;
    },
    publishedSurveyId() {
      return this.$store.state.event.survey.publishedSurveyId;
    },
    showDisabledClass() {
      return this.publishedSurveyId === this.surveyId && this.currentSurveyStatus === 'CLOSING';
    },
    timeLeftInClosingSurvey() {
      return this.survey?.timeLeftInClosingSurvey;
    },
    metaConfig() {
      if (this.isBroadcastSpeakerOrOrganizer) {
        let headerMainActionIconName = 'link';
        let headerMainActionButtonText = '@@translate@@ Launch Survey';
        let headerMainActionDisabled = !!this.publishedSurveyId && this.publishedSurveyId !== this.surveyId;
        let headerMainAction = () => {
          this.publishSurvey();
        };
        if (this.currentSurveyStatus === 'CLOSING' && this.publishedSurveyId === this.surveyId) {
          headerMainActionIconName = 'stopwatch';
          headerMainActionButtonText =
            this.timeLeftInClosingSurvey === ''
              ? '@@translate@@ Closing'
              : '@@translate@@ Closing in 00:' + this.timeLeftInClosingSurvey;

          headerMainActionDisabled = true;
          headerMainAction = () => Function.prototype;
        } else if (this.publishedSurveyId === this.surveyId) {
          headerMainActionIconName = '';
          headerMainActionButtonText = '@@translate@@ Close Survey';
          headerMainActionDisabled = false;
          headerMainAction = () => {
            this.unPublishSurvey();
          };
        }

        return {
          isSubmitButtonDisabled: true,
          headerMainActionDisabled,
          showHeaderMainAction: !this.isSurveyLoading,
          headerMainActionIconName,
          closeIconName: 'cross',
          closeButtonAriaLabel: '@@translate@@ Close',
          headerMainActionButtonText,
          headerMainAction
        };
      }

      return {
        headerMainActionDisabled: true,
        isSubmitButtonDisabled: this.currentSurveyStatus === 'SUBMITTING',
        showHeaderMainAction: this.timeLeftInClosingSurvey !== '',
        headerMainActionIconName: 'stopwatch',
        closeIconName: 'minimize',
        closeButtonAriaLabel: '@@translate@@ Close Survey',
        headerMainActionButtonText: '@@translate@@ Closing in 00:' + this.timeLeftInClosingSurvey,
        headerMainAction: () => Function.prototype
      };
    }
  },
  methods: {
    componentName(questionType) {
      if (questionType === 'SINGLE') {
        return 'SingleChoiceQuestion';
      }
      if (questionType === 'MULTI') {
        return 'MultipleChoiceQuestion';
      }
      if (questionType === 'RATING') {
        return 'RatingQuestion';
      }
      if (questionType === 'TEXT') {
        return 'FreeFormTextQuestion';
      }

      loggerService.error('No component found for questionType: ', questionType);
      return '';
    },
    unPublishSurvey() {
      if (!this.isBroadcastSpeakerOrOrganizer) {
        return;
      }

      this.$store.dispatch('event/survey/unPublishSurveyDialog', this.surveyId);
    },
    publishSurvey() {
      if (!this.isBroadcastSpeakerOrOrganizer) {
        return;
      }

      this.$store.dispatch('event/survey/showPublishSurveyDialog', this.surveyId);
    },
    closeAction() {
      this.$emit('cancel');

      if (!this.isBroadcastSpeakerOrOrganizer) {
        this.$store.dispatch('event/survey/minimizeSurvey', this.surveyId);
      } else {
        this.$store.commit('event/survey/clearPreviewSurveyId');
      }
    },
    onSubmitSurvey() {
      this.$store.dispatch('event/survey/submitSurvey', this.surveyId);
    }
  }
};
</script>

<style lang="scss" module>
.root {
  --max-height: calc(100vh - 130px);

  position: relative;
  width: 390px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  margin: -16px;
  height: var(--max-height);
  overflow: hidden;
  border-radius: 18px 18px 0 0;

  .bg {
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    z-index: -1;
    background: rgba(var(--primary-background-shade), var(--sidebar-alpha, 0.5));
    -webkit-backdrop-filter: blur(50px);
    backdrop-filter: blur(50px);
  }

  & .loading {
    display: flex;
    flex-direction: column;
    align-self: center;
    justify-content: center;
    align-items: center;
    height: var(--max-height);
    width: auto;
    & .updating {
      margin-top: 8px;
    }
  }

  & .headerAction {
    display: flex;
    height: auto;
    padding: 12px 20px 12px 36px;
    border-radius: 16px 16px 0 0;
    background: var(--theme-primary-color);
    justify-content: space-between;
    align-items: center;

    & .mainActionDisabled {
      cursor: default;
      opacity: 1;
    }

    & .actionButton {
      --size: 24px;
    }
  }

  & .content {
    overflow-y: auto;

    &::-webkit-scrollbar {
      width: 0;
    }

    & .header {
      height: auto;
      padding: 0 36px 24px;
      background: var(--theme-primary-color);
      display: flex;
      margin-bottom: 20px;
      flex-direction: column;
      justify-content: center;

      & .title {
        margin-bottom: 8px;
      }

      & .description {
        opacity: 0.8;
        font-weight: 100;
      }
    }

    & .divider {
      margin: 16px;
      border: 1px solid #ffffff;
      opacity: 0.5;
    }

    & .actionBar {
      display: flex;
      margin-top: 32px;
      margin-bottom: 16px;
      justify-content: center;

      & .submitButton {
        align-self: center;
      }
    }
  }

  & .submittedContent {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    flex: 1;
    padding: 32px 36px;
    text-align: center;
    gap: 16px;

    & .submittedIcon {
      width: 48px;
      height: 48px;
      border-radius: 50%;
      background: rgba(255, 255, 255, 0.2);
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 24px;
      color: var(--theme-primary-foreground-color, #fff);
    }

    & .submittedMessage {
      opacity: 0.9;
    }
  }
}

@media only screen and (max-width: $mobilebreakpoint) {
  .root {
    width: 100vw;
  }
}
</style>
